import {
  Component,
  EventEmitter,
  Input,
  Output,
  ViewChild,
} from "@angular/core";
import { formatMessage } from "devextreme/localization";
import { CalculatorUsFieldItemsService } from "../../../../../services/calculator-us-field-items.service";
import { UsCalculatorFieldItem } from "../../../../../model/calculator-us-remastered/usCalculatorFieldItem";
import { Publisher } from "../../../../../model/organization/organization";
import { DxAccordionComponent, DxFormComponent } from "devextreme-angular";
import { JobSpecification } from "../../../../../model/calculator-us-remastered/jobSpecification";

@Component({
  selector: "app-calculator-job-specification",
  templateUrl: "./calculator-job-specification.component.html",
  styleUrls: ["./calculator-job-specification.component.scss"],
})
export class CalculatorJobSpecificationComponent {
  @ViewChild("jobForm") jobForm: DxFormComponent;

  _selectedPublisher: Publisher;
  @Input()
  set selectedPublisher(val: Publisher | undefined) {
    if (val) {
      this._selectedPublisher = val;
      this.loadProductItems();
    }
    this.accordion?.instance.expandItem(val ? 0 : -1);
  }
  get selectedPublisher(): Publisher {
    return this._selectedPublisher;
  }

  _jobSpecification: JobSpecification;
  @Input()
  set jobSpecification(val: JobSpecification | undefined) {
    if (val) {
      this._jobSpecification = val;
    } else {
      this._jobSpecification = new JobSpecification();
    }
  }
  get jobSpecification(): JobSpecification {
    return this._jobSpecification;
  }

  @Output() onValueChanged = new EventEmitter<JobSpecification>();
  @Output() resetOverviewValues = new EventEmitter();

  @ViewChild("accordion", { static: false }) accordion!: DxAccordionComponent;

  formatMessage = formatMessage;
  collapsed: any = true;

  // Field items for dynamic dropdowns
  productFieldItems: UsCalculatorFieldItem[] = [];
  // selectedProductClassification: string = "";

  // Page field items loaded after product selection
  pageFieldItems: UsCalculatorFieldItem[] = [];

  // ⭐ Alle Hardcover/Softcover Options - gefüllt nach Produktauswahl
  initialReprintFieldItems: UsCalculatorFieldItem[] = [
    <UsCalculatorFieldItem>{
      displayValue: "Initial",
      keyValue: "INITIAL",
    },
    <UsCalculatorFieldItem>{
      displayValue: "Reprint",
      keyValue: "REPRINT",
    },
  ];
  selectedInitialReprint: UsCalculatorFieldItem | null = null;

  // Default values that should stay static (for comparison)
  staticDefaultWidth: string = "";
  staticDefaultHeight: string = "";
  staticDefaultInitialReprint: UsCalculatorFieldItem | null = null;

  get author() {
    return this._jobSpecification.author;
  }
  set author(val: string) {
    this._jobSpecification.author = val || "";
    this.onValueChanged.emit(this.jobSpecification);
  }

  get title() {
    return this._jobSpecification.title;
  }
  set title(val: string) {
    this._jobSpecification.title = val || "";
    this.onValueChanged.emit(this.jobSpecification);
  }

  _selectedProduct: UsCalculatorFieldItem | null = null;
  get selectedProduct() {
    return this._selectedProduct;
  }
  set selectedProduct(val: UsCalculatorFieldItem | null) {
    if (this._selectedProduct === val) return;
    console.log("set selectedProduct: ", val);
    this._selectedProduct = val;
    this.loadFieldItemsForProduct();
    this.jobSpecification.product = val?.keyValue || undefined;
    this.onValueChanged.emit(this.jobSpecification);
  }

  get width() {
    return this._jobSpecification.width;
  }
  set width(val: string) {
    this._jobSpecification.width = val || "";
    this.onValueChanged.emit(this.jobSpecification);
  }

  get height() {
    return this._jobSpecification.height;
  }
  set height(val: string) {
    this._jobSpecification.height = val || "";
    this.onValueChanged.emit(this.jobSpecification);
  }

  _selectedPage: UsCalculatorFieldItem | null = null;
  get selectedPage() {
    return this._selectedPage;
  }
  set selectedPage(val: UsCalculatorFieldItem | null) {
    this._selectedPage = val;
    this._jobSpecification.pages = val?.defaultValue || "";
    this.onValueChanged.emit(this.jobSpecification);
  }

  get quantity1() {
    return this._jobSpecification.quantity1;
  }
  set quantity1(val: string) {
    this._jobSpecification.quantity1 = val || "";
    this.onValueChanged.emit(this.jobSpecification);
  }

  get quantity2() {
    return this._jobSpecification.quantity2;
  }
  set quantity2(val: string) {
    this._jobSpecification.quantity2 = val || "";
    this.onValueChanged.emit(this.jobSpecification);
  }

  get quantity3() {
    return this._jobSpecification.quantity3;
  }
  set quantity3(val: string) {
    this._jobSpecification.quantity3 = val || "";
    this.onValueChanged.emit(this.jobSpecification);
  }

  _initialReprint: UsCalculatorFieldItem | null = null;
  get initialReprint() {
    return this._initialReprint;
  }
  set initialReprint(val: UsCalculatorFieldItem | null) {
    this._initialReprint = val;
    this._jobSpecification.initialReprint = val?.keyValue;
    this.onValueChanged.emit(this.jobSpecification);
  }

  get isbn() {
    return this._jobSpecification.isbn;
  }
  set isbn(val: string) {
    this._jobSpecification.isbn = val || "";
    this.onValueChanged.emit(this.jobSpecification);
  }

  constructor(
    private calculatorUsFieldItemsService: CalculatorUsFieldItemsService
  ) {}

  onAccordionClick(e) {
    // Prevent default scroll behavior when accordion expands/collapses
    const scrollPosition =
      window.pageYOffset || document.documentElement.scrollTop;

    this.collapsed = e.component.option("selectedIndex") == -1;

    // Restore scroll position after accordion state change
    setTimeout(() => {
      window.scrollTo(0, scrollPosition);
    }, 100);
  }

  private loadProductItems() {
    if (!this.selectedPublisher?.id) return;

    const publisherId = this.selectedPublisher.id;

    // Load only product field items initially
    this.calculatorUsFieldItemsService
      .getProductFieldItems(publisherId)
      .subscribe((items) => {
        this.selectedProduct = null;
        this.productFieldItems = items;
      });
  }

  private loadFieldItemsForProduct() {
    if (!this.selectedPublisher?.id || !this.selectedProduct?.keyValue) return;

    this.resetFieldItems();

    const publisherId = this.selectedPublisher.id;
    const classification = this.selectedProduct?.keyValue;

    this.calculatorUsFieldItemsService
      .getFieldItemsBySubcategory(
        publisherId,
        this.calculatorUsFieldItemsService.getDbClassification(classification),
        "JOB_SPECIFICATION"
      )
      .subscribe((item) => {
        for (const fieldItem of item) {
          if (fieldItem.inputName === "WIDTH" && fieldItem.defaultValue) {
            this.staticDefaultWidth = this.width = fieldItem.defaultValue;
          } else if (
            fieldItem.inputName === "HEIGHT" &&
            fieldItem.defaultValue
          ) {
            this.staticDefaultHeight = this.height = fieldItem.defaultValue;
          } else if (
            fieldItem.inputName === "INITIAL_REPRINT" &&
            fieldItem.keyValue
          ) {
            this.initialReprintFieldItems.push(fieldItem);
            if (fieldItem.isDefault)
              this.staticDefaultInitialReprint =
                this.initialReprint =
                this.selectedInitialReprint =
                  fieldItem;
          }
        }
      });

    if (
      classification?.includes("HARDCOVER") ||
      classification?.includes("SOFTCOVER")
    ) {
      // Load basic page field items as fallback
      this.calculatorUsFieldItemsService
        .getPageValuesForProductType(
          publisherId,
          this.calculatorUsFieldItemsService.getDbClassification(classification)
        )
        .subscribe((items) => {
          this.pageFieldItems = items;
          // Set default page if available
          this.selectedPage = items.find((item) => item.isDefault) || null;
        });
    }
  }

  public initializeFieldItems() {
    this.resetFieldItems();
    this.initialReprintFieldItems = [
      <UsCalculatorFieldItem>{
        displayValue: "Initial",
        keyValue: "INITIAL",
      },
      <UsCalculatorFieldItem>{
        displayValue: "Reprint",
        keyValue: "REPRINT",
      },
    ];
  }

  private resetFieldItems() {
    this.pageFieldItems = [];
    this.selectedPage = null;

    // Reset dimensions
    this.width = "";
    this.height = "";

    // Reset Hardcover/Softcover Felder (Sub-Komponenten verwalten sich selbst)
    this.initialReprintFieldItems = [];
    this.selectedInitialReprint = null;

    this.resetOverviewValues.emit();
  }
}
